import { useState } from 'react';

import {
  GetFormalOffersResponse,
  GetIwocapayPaymentLaterResponse,
} from '@iwoca/lapi-client/edge';
import { Button, Skeleton, Table } from '@iwoca/orion';

import { useDrawDownRepaymentScheduleIwocaPay } from './useDrawDownRepaymentScheduleIwocaPay';
import { displayCurrency } from '../../../../utils/DisplayCurrency';
import { formatRate } from '../../utils/currency';
import { formatDate } from '../../utils/date';
import { mapOfferDurationToMonths } from '../../utils/mapOfferDurationToMonths';

const COLLAPSED_ROW_COUNT = 3;

export const SelectedOfferSchedule = ({
  offer,
  payLink,
}: {
  offer: NonNullable<GetFormalOffersResponse['data']>['formal_offers'][number];
  payLink: GetIwocapayPaymentLaterResponse['data'][number];
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const {
    drawDownRepaymentScheduleIwocapay,
    interestRate,
    loadingDrawDownRepaymentScheduleIwocapay,
  } = useDrawDownRepaymentScheduleIwocaPay({ offer, payLink });

  if (loadingDrawDownRepaymentScheduleIwocapay) return <Skeleton />;

  if (!drawDownRepaymentScheduleIwocapay) return null;

  const { schedule, summary } = drawDownRepaymentScheduleIwocapay;

  if (!schedule || !summary) return null;

  const durationMonths = mapOfferDurationToMonths(offer);
  const isCollapsible = durationMonths === 12;
  const visibleSchedule =
    isCollapsible && !isExpanded
      ? schedule.slice(0, COLLAPSED_ROW_COUNT)
      : schedule;

  const totalPrincipal = schedule.reduce(
    (total, entry) => total + entry.principal,
    0,
  );

  return (
    <div className="mb-xl flex flex-col gap-m">
      <div className="flex flex-col gap-xs py-m">
        <h3 className="m-0 text-l font-bold text-primary-10">
          Your pending payment schedule
        </h3>
        <p className="m-0 text-m font-reg text-secondary-40">
          If you complete your security checks today, your payment schedule
          would be as follows
        </p>
      </div>
      <Table
        pinFirstCol
        data={visibleSchedule}
        columns={[
          {
            id: 'date',
            header: 'Date',
            cell: ({ row }) => <>{formatDate(row.original.date)}</>,
            footer: 'Total',
          },
          {
            id: 'principal',
            header: 'Invoice amount',
            cell: ({ row }) => <>{displayCurrency(row.original.principal)}</>,
            footer: () => <>{displayCurrency(totalPrincipal)}</>,
          },
          {
            id: 'interest',
            header: 'Interest*',
            cell: ({ row }) => <>{displayCurrency(row.original.interest)}</>,
            footer: () => <>{displayCurrency(summary.total_interest!)}</>,
          },
          {
            id: 'total',
            header: 'Total',
            cell: ({ row }) => <>{displayCurrency(row.original.total)}</>,
            footer: () => <>{displayCurrency(summary.total_payable!)}</>,
          },
        ]}
      />
      {isCollapsible && (
        <Button
          type="button"
          variant="tertiary"
          size="small"
          onClick={() => setIsExpanded(!isExpanded)}
          className="mt-2"
        >
          {isExpanded ? 'Close full schedule —' : 'View full schedule +'}
        </Button>
      )}
      <p className="m-0 text-center text-s text-secondary-40">
        *Your interest rate is{' '}
        {interestRate !== undefined ? formatRate(interestRate) : '0.0%'} per
        month.
      </p>
    </div>
  );
};
